import React from "react";
import { Col, Form, Row } from 'react-bootstrap';
import TypedSelect from "common/ui/TypedSelect";
import { ProducerAccount } from "./producerAccount";
import "./AddProducerAccountPermissionModal.scss";
import { AllProducerAccountPermissions, ProducerAccountPermission, getProducerAccountPermissionText } from "./producerAccountPermission";
import { localizeProducer as localize } from "features/localization/localizeService";

export interface AddProducerAccountPermissionModalProps {
    producerAccounts: ProducerAccount[];
    onAccountsChanged(producerAccounts: ProducerAccount[]): void;
}

const AddProducerAccountPermissionModal: React.FC<AddProducerAccountPermissionModalProps> = props => {

    function onAddAccountClicked() {
        const newAccount = { ...new ProducerAccount() };
        setProducerAccountPermissions(newAccount, ProducerAccountPermission.PrimaryUser);
        const updatedAccounts = props.producerAccounts != null ? [...props.producerAccounts, newAccount] : [newAccount];
        props.onAccountsChanged(updatedAccounts);
    }

    function onRemoveAccountClicked(account: ProducerAccount) {
        const updatedAccounts = props.producerAccounts?.filter(acc => acc.accountId !== account.accountId)
        props.onAccountsChanged(updatedAccounts);
    }

    function onAccountChanged(index: number, account: ProducerAccount) {
        const updatedAccounts = props.producerAccounts?.map((acc, idx) => index === idx ? account : acc)
        props.onAccountsChanged(updatedAccounts);
    }

    function onPermissionChanged(account: ProducerAccount, permission: ProducerAccountPermission) {
        setProducerAccountPermissions(account, permission);
        const updatedAccounts = props.producerAccounts?.map(acc => acc.accountId === account.accountId ? account : acc);
        props.onAccountsChanged(updatedAccounts);
    }

    function getProducerAccountPermission(account: ProducerAccount): ProducerAccountPermission {
        const viewSign = account.canView && account.canSign;
        const makeViewSign = account.canManage && viewSign;

        if (account.isPrimaryUser) return ProducerAccountPermission.PrimaryUser;
        else if (makeViewSign) return ProducerAccountPermission.MakeViewSign;
        else if (viewSign) return ProducerAccountPermission.ViewSign;
        else return ProducerAccountPermission.ViewOnly;
    }

    function setProducerAccountPermissions(account: ProducerAccount, permission: ProducerAccountPermission) {
        switch (permission) {
            case ProducerAccountPermission.PrimaryUser:
                account.isPrimaryUser = true;
                account.canManage = true;
                account.canView = true;
                account.canSign = true;
                break;
            case ProducerAccountPermission.MakeViewSign:
                account.isPrimaryUser = false;
                account.canManage = true;
                account.canView = true;
                account.canSign = true;
                break;
            case ProducerAccountPermission.ViewSign:
                account.isPrimaryUser = false;
                account.canManage = false;
                account.canView = true;
                account.canSign = true;
                break;
            case ProducerAccountPermission.ViewOnly:
                account.isPrimaryUser = false;
                account.canManage = false;
                account.canView = true;
                account.canSign = false;
                break;
            default:
                break;
        }
    }

    return <div className="add-producer-account-permission-modal">{props.producerAccounts?.map((acc, idx) =>
        <Form.Group as={Row} key={idx} className="mb-2">
            <Col xs="4" className="account-details">
                <Form.Control
                    defaultValue={acc.accountId}
                    placeholder={localize("addProducerAccountPermissionModal_Placeholder_AddAccountId")}
                    required
                    onBlur={(ev: React.ChangeEvent<HTMLInputElement>) => onAccountChanged(idx, { ...acc, accountId: ev.target.value })} />
            </Col>
            <Col xs="4" className="account-details">
                <Form.Control
                    defaultValue={acc.accountName}
                    placeholder={localize("addProducerAccountPermissionModal_Placeholder_AddAccountName")}
                    onBlur={(ev: React.ChangeEvent<HTMLInputElement>) => onAccountChanged(idx, { ...acc, accountName: ev.target.value })} />
            </Col>
            <Col xs="3" className="account-permission">
                <TypedSelect
                    value={getProducerAccountPermission(acc)}
                    options={AllProducerAccountPermissions}
                    valueGetter={m => m}
                    textGetter={m => getProducerAccountPermissionText(m)}
                    onValueSelected={m => onPermissionChanged(acc, m)} />
            </Col>
            <Col xs="1">
                <button className='link-button mr-auto float-right' onClick={() => onRemoveAccountClicked(acc)}>
                    <span className="fa fa-remove remove-account"></span>
                </button>
            </Col>
        </Form.Group>)}
        <Form.Group as={Row}>
            <Col xs="4" className="pr-0">
                <button type="button" className='link-button mr-auto' onClick={onAddAccountClicked}>
                    {localize("addProducerAccountPermissionModal_Button_AddAccount")}
                </button>
            </Col>
        </Form.Group>
    </div>;
}

export default AddProducerAccountPermissionModal;